import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import IndividualCard from '../../components/IndividualCard/IndividualCard';
import ContactUs from '../../components/ContactUs/ContactUs';
import styles from './HymnsPage.module.css';
import config from '../../config/config';
import { HymnPostSanity, HymnCardData } from '../../types/types';

const HymnsPage: React.FC = () => {
  const [hymnCards, setHymnCards] = useState<HymnCardData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to extract text from the first block of portable text
  const getPortableTextSummary = (portableTextBlocks: any): string => {
    if (!portableTextBlocks || !Array.isArray(portableTextBlocks) || portableTextBlocks.length === 0) {
      return 'No explanation available.';
    }
    
    const firstBlock = portableTextBlocks[0];
    if (firstBlock && firstBlock.children && Array.isArray(firstBlock.children) && firstBlock.children.length > 0) {
      return firstBlock.children
        .filter((child: any) => child.text)
        .map((child: any) => child.text)
        .join(' ');
    }
    
    return 'Explanation not in expected format.';
  };

  // Helper function to get a snippet of lyrics
  const getLyricsSnippet = (lyrics: string, maxLength: number = 150): string => {
    if (!lyrics) return 'No lyrics available.';
    
    if (lyrics.length <= maxLength) return lyrics;
    
    // Try to find a natural break point (like a period or line break)
    const breakPoint = lyrics.substring(0, maxLength).lastIndexOf('.');
    if (breakPoint > maxLength / 2) {
      return lyrics.substring(0, breakPoint + 1);
    }
    
    // If no good break point, just truncate with ellipsis
    return lyrics.substring(0, maxLength) + '...';
  };

  useEffect(() => {
    setLoading(true);
    
    // Fetch hymn posts from Sanity.io
    const fetchHymnPosts = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/hymns`);
        
        if (response.data && response.data.length > 0) {
          // Sort fetched posts by ID (assuming newer hymns have higher IDs)
          const sortedPosts = response.data.sort((a: HymnPostSanity, b: HymnPostSanity) => 
            b.id - a.id
          );
          
          // Transform the posts to match the HymnCardData interface
          const formattedHymns: HymnCardData[] = sortedPosts.map((post: HymnPostSanity) => ({
            id: post.id,
            title: post.title,
            lyricsSnippet: getLyricsSnippet(post.lyrics),
            explanationSnippet: getPortableTextSummary(post.explanation),
            author: post.author,
            year: post.year
          }));
          
          setHymnCards(formattedHymns);
          setError(null);
        } else {
          setError('No hymn posts found.');
          setHymnCards([]);
        }
      } catch (error) {
        console.error('Error fetching hymn posts:', error);
        setError('Failed to fetch hymn posts. Please try again later.');
        
        // Only use mock data if enabled in config
        if (config.USE_MOCK_DATA) {
          // Mock data would go here if needed
          const mockHymns: HymnCardData[] = [
            {
              id: 1,
              title: 'Amazing Grace',
              lyricsSnippet: 'Amazing grace! How sweet the sound, That saved a wretch like me! I once was lost, but now am found, Was blind, but now I see.',
              explanationSnippet: 'Written by John Newton (1779), this beloved hymn expresses the profound transformation of God\'s grace in a sinner\'s life.',
              author: 'John Newton',
              year: '1779'
            },
            {
              id: 2,
              title: 'Great Is Thy Faithfulness',
              lyricsSnippet: 'Great is Thy faithfulness, O God my Father, There is no shadow of turning with Thee; Thou changest not, Thy compassions, they fail not; As Thou hast been Thou forever wilt be.',
              explanationSnippet: 'Composed by Thomas O. Chisholm (1923), this hymn celebrates God\'s unchanging character and daily faithfulness to His people.',
              author: 'Thomas O. Chisholm',
              year: '1923'
            },
            {
              id: 3,
              title: 'How Great Thou Art',
              lyricsSnippet: 'O Lord my God, when I in awesome wonder Consider all the worlds Thy hands have made, I see the stars, I hear the rolling thunder, Thy power throughout the universe displayed.',
              explanationSnippet: 'Originally a Swedish poem by Carl Boberg (1885) translated by Stuart K. Hine (1949), this hymn expresses wonder at God\'s creation and redemptive work.',
              author: 'Carl Boberg / Stuart K. Hine',
              year: '1885/1949'
            }
          ];
          setHymnCards(mockHymns);
        } else {
          setHymnCards([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHymnPosts();
  }, []);

  if (loading) return <div className={styles.loading}>Loading hymns...</div>;
  if (error && hymnCards.length === 0) return <div className={styles.error}>{error}</div>;
  if (hymnCards.length === 0) return <div className={styles.empty}>No hymns found.</div>;

  return (
    <div className={styles['hymns-page']}>
      <section className={styles['hymns-intro']}>
        <h1>Timeless Hymns of Faith</h1>
        <p className={styles['hymns-description']}>
          Hymns have been a cornerstone of Christian worship for centuries, carrying rich theological truths
          and expressions of faith. Explore these timeless compositions that continue to inspire believers today.
        </p>
      </section>

      <section className={styles['hymns-verses']}>
        <h2>Classic Hymns and Their Stories</h2>
        <p className={styles['click-instruction']}>Click on any hymn to view its detailed history</p>
        <div className={styles['verses-container']}>
          {hymnCards.map((hymn) => (
            <Link 
              to={`/hymns/${hymn.id}`}
              key={hymn.id}
              className={styles['verse-link']}
            >
              <div className={`${styles['verse-card']} ${styles['clickable']}`}>
                <IndividualCard
                  reference={hymn.title}
                  verses={hymn.lyricsSnippet}
                  explanation={hymn.explanationSnippet}
                  date={hymn.year}
                />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <ContactUs />
    </div>
  );
};

export default HymnsPage;
